import { useState, useEffect, useRef } from 'react';
import { videoApi } from '@/services/api';
import { toast } from 'sonner';

interface UploadedVideoPlayerProps {
  src: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
}

/**
 * Video player for uploaded content stored in MinIO / S3 / any storage.
 * Always routes through the backend proxy so it works regardless of how the
 * URL was stored (MinIO :9100, ngrok, localhost /storage/..., etc.).
 * Blob URLs (local browser objects) are passed through unchanged.
 */
export function UploadedVideoPlayer({ src, className, autoPlay = false, muted = false }: UploadedVideoPlayerProps) {
  const [videoSrc, setVideoSrc] = useState(() => videoApi.getUploadedMediaUrl(src));
  const [hasErrored, setHasErrored] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setVideoSrc(videoApi.getUploadedMediaUrl(src));
    setHasErrored(false);
  }, [src]);

  const handleError = () => {
    if (!hasErrored) {
      setHasErrored(true);
      toast.error('Failed to play video');
    }
  };

  return (
    <video
      ref={videoRef}
      src={videoSrc}
      className={className}
      controls
      autoPlay={autoPlay}
      muted={muted}
      playsInline
      onError={handleError}
    />
  );
}
